import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import {
    Alert,
    Box,
    Button,
    Divider,
    List,
    ListItem,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AuthSplitLayout from './AuthSplitLayout';
import { getOAuthQueryFromSearch } from './auth-utils';
import { betterAuthClient } from '@/libs/better-auth-client';

type OrganizationItem = {
    id: string;
    name: string;
    slug?: string;
};

type BetterAuthResult<TData> = {
    data?: TData | null;
    error?: {
        message?: string;
    } | null;
};

export default function AuthSelectWorkspace() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [organizations, setOrganizations] = useState<OrganizationItem[]>([]);
    const [submittingOrgId, setSubmittingOrgId] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        void betterAuthClient.organization
            .list()
            .then((result: BetterAuthResult<OrganizationItem[]>) => {
                if (result.error) {
                    enqueueSnackbar(
                        typeof result.error.message === 'string'
                            ? result.error.message
                            : t('auth.oauth.error.default'),
                    );
                    return;
                }

                const rows = result.data || [];
                setOrganizations(rows);
                // Do NOT auto-select a single workspace: granting an OAuth client access to a workspace
                // is a deliberate choice, so the user always explicitly confirms which one — even when
                // they only have one.
            })
            .catch(() => {
                enqueueSnackbar(t('auth.oauth.error.default'));
            })
            .finally(() => {
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectOrganization = async (organizationId: string) => {
        setSubmittingOrgId(organizationId);
        try {
            const setActiveResult: BetterAuthResult<unknown> = await betterAuthClient.organization.setActive({
                organizationId,
            });
            if (setActiveResult.error) {
                enqueueSnackbar(
                    typeof setActiveResult.error.message === 'string'
                        ? setActiveResult.error.message
                        : t('auth.oauth.error.default'),
                );
                return;
            }

            // Forward the signed OAuth query so /oauth2/continue can rebuild the pending authorization
            // (oAuthState). Without it the server responds 400 "missing oauth query".
            const oauthQuery = getOAuthQueryFromSearch();
            const continueResult: BetterAuthResult<{ url?: string }> = await betterAuthClient.oauth2.continue({
                postLogin: true,
                ...(oauthQuery ? { oauth_query: oauthQuery } : {}),
            });
            if (continueResult.error) {
                enqueueSnackbar(
                    typeof continueResult.error.message === 'string'
                        ? continueResult.error.message
                        : t('auth.oauth.error.default'),
                );
                return;
            }

            const redirectUrl = continueResult.data?.url;
            if (redirectUrl) {
                window.location.href = redirectUrl;
                return;
            }

            enqueueSnackbar(t('auth.oauth.error.redirect-missing'));
        } catch {
            enqueueSnackbar(t('auth.oauth.error.default'));
        } finally {
            setSubmittingOrgId(null);
        }
    };

    return (
        <AuthSplitLayout>
            <Stack spacing={3}>
                <Box>
                    <Typography
                        component={Link}
                        to="/auth/login"
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '&:hover': { color: 'primary.main' },
                        }}
                    >
                        <ArrowBackRounded sx={{ fontSize: 16 }} />
                        {t('auth.oauth.back-to-login')}
                    </Typography>
                </Box>

                <Box>
                    <Typography variant="h5" fontWeight={700} mb={0.5}>
                        {t('auth.oauth.select-workspace.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('auth.oauth.select-workspace.subtitle')}
                    </Typography>
                </Box>

                {loading && (
                    <Typography variant="body2" color="text.secondary">
                        {t('auth.common.loading')}
                    </Typography>
                )}

                {!loading && organizations.length === 0 && (
                    <Alert severity="error">{t('auth.oauth.select-workspace.empty')}</Alert>
                )}

                {!loading && organizations.length > 0 && (
                    <List disablePadding>
                        {organizations.map((organization, index) => (
                            <Box key={organization.id}>
                                {index > 0 && <Divider />}
                                <ListItem
                                    disableGutters
                                    secondaryAction={
                                        <Button
                                            variant="contained"
                                            size="small"
                                            disabled={submittingOrgId !== null}
                                            onClick={() => selectOrganization(organization.id)}
                                        >
                                            {submittingOrgId === organization.id
                                                ? t('auth.common.loading')
                                                : t('auth.oauth.select-workspace.choose')}
                                        </Button>
                                    }
                                >
                                    <ListItemText
                                        primary={organization.name}
                                        secondary={organization.slug || undefined}
                                        sx={{ pr: 16 }}
                                    />
                                </ListItem>
                            </Box>
                        ))}
                    </List>
                )}
            </Stack>
        </AuthSplitLayout>
    );
}
